import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import {
  FiZoomIn,
  FiZoomOut,
  FiMaximize2,
  FiRotateCcw,
  FiRotateCw,
  FiDownload,
  FiUpload,
  FiPlus,
  FiPlay,
  FiPause,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { PitchBar, PitchMap } from './practiceTypes';
import {
  setPracticeVerification,
  setResourceVerification,
  saveVerifiedAI,
  SaveTarget,
} from './practiceApi';

type Props = {
  pitchMap: PitchMap;
  audioFileName?: string;
  onSaved: () => void;
  isAIRecording: boolean;
  isVerified: boolean;
  // Which variant (resource) this save targets.
  saveTarget?: SaveTarget;
  // Variant selector (module has multiple ai/recording variants).
  variants?: { id: string | number; label: string }[];
  activeVariantId?: string | number | null;
  onSelectVariant?: (id: string | number) => void;
  resourceId?: string | number;
  isResourceVerified?: boolean;
  onResourceVerificationChanged?: (isVerified: boolean) => void;
};

// ---- music helpers ----
const A4 = 440;
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const freqToMidi = (f: number) => 69 + 12 * Math.log2(f / A4);
const midiToFreq = (m: number) => A4 * Math.pow(2, (m - 69) / 12);
const midiToName = (m: number) => {
  const r = Math.round(m);
  return `${NOTE_NAMES[((r % 12) + 12) % 12]}${Math.floor(r / 12) - 1}`;
};
// Effective midi for a bar, including the editor's cents fine-tune.
const barMidi = (b: PitchBar) => freqToMidi(b.avgs) + (b.cents || 0) / 100;
const displayNote = (b: PitchBar) => (b.note && b.note.trim() ? b.note : midiToName(barMidi(b)));
const fmtTime = (t: number) => {
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// ---- teacher ↔ student pairing (k-th teacher ↔ k-th student, by time) ----
const pairedStudentId = (bs: PitchBar[], teacherId: number): number | null => {
  const all = [...bs].sort((a, b) => a.starts - b.starts);
  const teachers = all.filter((b) => b.sangBy === 'teacher');
  const students = all.filter((b) => b.sangBy === 'student');
  const idx = teachers.findIndex((b) => b.id === teacherId);
  if (idx < 0 || idx >= students.length) return null;
  return students[idx].id;
};

// Merge two bars (by id) into one, returning the new bar list.
const mergeTwo = (bs: PitchBar[], idA: number, idB: number): PitchBar[] => {
  const a = bs.find((x) => x.id === idA);
  const b = bs.find((x) => x.id === idB);
  if (!a || !b) return bs;
  const first = a.starts <= b.starts ? a : b;
  const second = first === a ? b : a;
  const aDur = a.ends - a.starts;
  const bDur = b.ends - b.starts;
  const merged: PitchBar = {
    ...first,
    starts: Math.min(a.starts, b.starts),
    ends: Math.max(a.ends, b.ends),
    avgs: (a.avgs * aDur + b.avgs * bDur) / (aDur + bDur || 1),
    lyric:
      first.lyric === second.lyric ? first.lyric : `${first.lyric}${second.lyric}`.trim(),
  };
  return bs.filter((x) => x.id !== a.id && x.id !== b.id).concat(merged);
};

// ---- layout constants ----
const DEFAULT_PX_PER_SEC = 180; // default time scale
const DEFAULT_PX_PER_SEMI = 20; // default pitch scale
const BAR_H = 14;
const PAD_TOP = 24;
const PAD_BOTTOM = 28;
const SEMI_PAD = 2; // extra semitones above/below range
const MIN_DUR = 0.05;

const PitchMapEditor: React.FC<Props> = ({
  pitchMap,
  audioFileName,
  onSaved,
  isAIRecording,
  isVerified,
  saveTarget,
  variants,
  activeVariantId,
  onSelectVariant,
  resourceId,
  isResourceVerified,
  onResourceVerificationChanged,
}) => {
  const [bars, setBars] = React.useState<PitchBar[]>(
    [...(pitchMap.bars_data || [])].sort((a, b) => a.starts - b.starts)
  );
  // Sections/weights ride along (updated when a JSON is uploaded).
  const [extra, setExtra] = React.useState({
    student_sections_data: pitchMap.student_sections_data,
    student_section_weights: pitchMap.student_section_weights,
  });
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [pxPerSec, setPxPerSec] = React.useState(DEFAULT_PX_PER_SEC);
  // Pitch axis auto-fits the visible chart height; pitchZoom multiplies that fit.
  const [pitchZoom, setPitchZoom] = React.useState(1);
  const [chartViewportH, setChartViewportH] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [playhead, setPlayhead] = React.useState(0);
  const [audioDur, setAudioDur] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [downloadingAudio, setDownloadingAudio] = React.useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = React.useState(false);
  const [verifying, setVerifying] = React.useState(false);
  const canSavePitchMap =
    !!saveTarget?.aiRecordingType &&
    saveTarget.aiRecordingType !== 'none' &&
    !!saveTarget.pitchTempo;
  const canVerifyResource = resourceId != null && resourceId !== '';

  // ---- undo / redo history ----
  const HISTORY_LIMIT = 100;
  const [past, setPast] = React.useState<PitchBar[][]>([]);
  const [future, setFuture] = React.useState<PitchBar[][]>([]);
  const barsRef = React.useRef(bars);
  React.useEffect(() => {
    barsRef.current = bars;
  }, [bars]);
  const pendingRef = React.useRef<PitchBar[] | null>(null);
  const cloneBars = (bs: PitchBar[]) => bs.map((b) => ({ ...b }));
  // Unconditional push, for discrete actions that always change state.
  const pushHistory = () => {
    setPast((p) => [...p, cloneBars(barsRef.current)].slice(-HISTORY_LIMIT));
    setFuture([]);
  };
  // Capture the pre-edit state once at the start of an interaction.
  const snapshot = () => {
    if (pendingRef.current == null) pendingRef.current = cloneBars(barsRef.current);
  };
  // Push the captured snapshot onto the undo stack if anything actually changed.
  const commitHistory = () => {
    const snap = pendingRef.current;
    pendingRef.current = null;
    if (!snap) return;
    if (JSON.stringify(snap) === JSON.stringify(barsRef.current)) return;
    setPast((p) => [...p, snap].slice(-HISTORY_LIMIT));
    setFuture([]);
  };
  const undo = () => {
    if (!past.length) return;
    const prev = past[past.length - 1];
    setFuture((f) => [cloneBars(barsRef.current), ...f].slice(0, HISTORY_LIMIT));
    setBars(prev);
    setPast((p) => p.slice(0, -1));
    setSelectedId(null);
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setPast((p) => [...p, cloneBars(barsRef.current)].slice(-HISTORY_LIMIT));
    setBars(next);
    setFuture((f) => f.slice(1));
    setSelectedId(null);
  };

  const rafRef = React.useRef<number | null>(null);
  const lastTsRef = React.useRef<number | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const hasAudio = !!pitchMap.audioUrl;

  // sorted view + derived ranges
  const sorted = React.useMemo(
    () => [...bars].sort((a, b) => a.starts - b.starts),
    [bars]
  );
  const hasBars = sorted.length > 0;
  const barsDuration = React.useMemo(
    () => sorted.reduce((mx, b) => Math.max(mx, b.ends), 0) || 1,
    [sorted]
  );
  // Timeline spans the longer of the bars or the audio track (default when empty).
  const duration = Math.max(barsDuration, audioDur, hasBars ? 0 : 30);
  // Pitch range — falls back to a default octave-ish window when there are no bars.
  const { minMidi, maxMidi } = React.useMemo(() => {
    if (!sorted.length) return { minMidi: 52, maxMidi: 76 };
    const ms = sorted.map(barMidi);
    return {
      minMidi: Math.min(...ms) - SEMI_PAD,
      maxMidi: Math.max(...ms) + SEMI_PAD,
    };
  }, [sorted]);

  // Fit the pitch range into the visible chart height (×pitchZoom).
  const range = Math.max(1, maxMidi - minMidi);
  const fitPxPerSemi =
    chartViewportH > 0 ? (chartViewportH - PAD_TOP - PAD_BOTTOM) / range : DEFAULT_PX_PER_SEMI;
  const pxPerSemi = Math.max(6, Math.min(80, fitPxPerSemi * pitchZoom));
  const chartHeight = range * pxPerSemi + PAD_TOP + PAD_BOTTOM;

  // Track the chart viewport height so the pitch axis can fit it.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setChartViewportH(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const chartWidth = duration * pxPerSec + 40;
  const xOf = (t: number) => t * pxPerSec;
  const yOf = (midi: number) => PAD_TOP + (maxMidi - midi) * pxPerSemi;

  const selected = sorted.find((b) => b.id === selectedId) || null;
  const selectedIndex = sorted.findIndex((b) => b.id === selectedId);

  // Bar at the playhead → drives the prev/current/next lyric strip.
  const activeIndex = React.useMemo(() => {
    if (!sorted.length) return -1;
    let idx = sorted.findIndex((b) => playhead >= b.starts && playhead < b.ends);
    if (idx === -1) {
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].starts <= playhead) {
          idx = i;
          break;
        }
      }
    }
    return idx === -1 ? 0 : idx;
  }, [sorted, playhead]);
  const lyricOf = (b?: PitchBar) => (b ? b.lyrics_hindi || b.lyric || '' : '');
  // When a teacher bar is selected, mark its mirrored student bar.
  const mirrorId =
    selected?.sangBy === 'teacher' ? pairedStudentId(sorted, selected.id) : null;

  // Smoothly follow the playhead while playing: keep it parked ~30% from the
  // left and let the chart scroll continuously underneath it (no jumps).
  const keepPlayheadVisible = (t: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const target = el.clientWidth * 0.3;
    const desired = xOf(t) - target;
    const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = Math.max(0, Math.min(maxScroll, desired));
  };

  // ---- playback ----
  const stop = () => {
    setPlaying(false);
    if (hasAudio) audioRef.current?.pause();
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    lastTsRef.current = null;
  };

  const play = () => {
    if (playhead >= duration) {
      setPlayhead(0);
      if (audioRef.current) audioRef.current.currentTime = 0;
    }
    if (hasAudio && audioRef.current) {
      const p = audioRef.current.play();
      if (p && typeof p.then === 'function') {
        p.then(() => setPlaying(true)).catch((err) => {
          console.error('Audio play failed:', err);
          toast.error('Could not play audio. See console for details.');
          setPlaying(false);
        });
        return;
      }
    }
    setPlaying(true);
  };

  const seek = (t: number) => {
    const clamped = Math.min(duration, Math.max(0, t));
    setPlayhead(clamped);
    if (audioRef.current) audioRef.current.currentTime = clamped;
    keepPlayheadVisible(clamped);
  };

  React.useEffect(() => {
    if (!playing) return;
    const tick = (ts: number) => {
      if (hasAudio && audioRef.current) {
        const t = audioRef.current.currentTime;
        setPlayhead(t);
        keepPlayheadVisible(t);
        if (audioRef.current.ended) {
          stop();
          return;
        }
      } else {
        if (lastTsRef.current != null) {
          const dt = (ts - lastTsRef.current) / 1000;
          setPlayhead((p) => {
            const next = p + dt;
            if (next >= duration) {
              stop();
              return duration;
            }
            keepPlayheadVisible(next);
            return next;
          });
        }
        lastTsRef.current = ts;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, duration]);

  // ---- editing ops (teacher edits mirror to the paired student bar) ----
  const updateBar = (id: number, patch: Partial<PitchBar>) =>
    setBars((prev) => {
      const bar = prev.find((b) => b.id === id);
      if (!bar) return prev;
      let next = prev.map((b) => (b.id === id ? { ...b, ...patch } : b));
      if (bar.sangBy === 'teacher') {
        const sid = pairedStudentId(prev, id);
        if (sid != null) {
          const updatedTeacher = { ...bar, ...patch };
          next = next.map((b) => {
            if (b.id !== sid) return b;
            const mirror: Partial<PitchBar> = {};
            if ('note' in patch) mirror.note = updatedTeacher.note;
            if ('cents' in patch) mirror.cents = updatedTeacher.cents;
            if ('lyric' in patch) mirror.lyric = updatedTeacher.lyric;
            if ('lyrics_hindi' in patch) mirror.lyrics_hindi = updatedTeacher.lyrics_hindi;
            // Mirror duration, keeping the student's own start time.
            if ('starts' in patch || 'ends' in patch) {
              mirror.ends = b.starts + (updatedTeacher.ends - updatedTeacher.starts);
            }
            return { ...b, ...mirror };
          });
        }
      }
      return next;
    });

  const deleteBar = (id: number) => {
    pushHistory();
    setBars((prev) => {
      const bar = prev.find((b) => b.id === id);
      const remove = new Set<number>([id]);
      if (bar?.sangBy === 'teacher') {
        const sid = pairedStudentId(prev, id);
        if (sid != null) remove.add(sid);
      }
      return prev.filter((b) => !remove.has(b.id));
    });
    setSelectedId(null);
  };

  // Add a new bar at the playhead (mid-pitch), then select it for editing.
  const addBar = () => {
    const midMidi = Math.round((minMidi + maxMidi) / 2);
    const dur = 0.6;
    const startsAt = Math.max(0, Math.min(playhead, Math.max(0, duration - dur)));
    const newId = bars.reduce((mx, b) => Math.max(mx, Number(b.id) || 0), 0) + 1;
    const newBar: PitchBar = {
      id: newId,
      starts: +startsAt.toFixed(2),
      ends: +(startsAt + dur).toFixed(2),
      avgs: midiToFreq(midMidi),
      note: midiToName(midMidi),
      lyric: '',
      sangBy: 'teacher',
    };
    pushHistory();
    setBars((prev) => [...prev, newBar]);
    setSelectedId(newId);
  };

  const mergeAt = (index: number, dir: -1 | 1) => {
    const a = sorted[index];
    const b = sorted[index + dir];
    if (!a || !b) return;
    pushHistory();
    setBars((prev) => {
      let next = mergeTwo(prev, a.id, b.id);
      // Mirror the merge onto the paired student bars.
      if (a.sangBy === 'teacher' && b.sangBy === 'teacher') {
        const sa = pairedStudentId(prev, a.id);
        const sb = pairedStudentId(prev, b.id);
        if (sa != null && sb != null && sa !== sb) next = mergeTwo(next, sa, sb);
      }
      return next;
    });
    setSelectedId(a.starts <= b.starts ? a.id : b.id);
  };

  // ---- resize via drag handles ----
  const dragRef = React.useRef<{
    id: number;
    edge: 'left' | 'right';
    startX: number;
    orig: number;
    min: number;
    max: number;
  } | null>(null);

  const onHandleDown = (
    e: React.PointerEvent,
    bar: PitchBar,
    edge: 'left' | 'right',
    idx: number
  ) => {
    e.stopPropagation();
    snapshot();
    const prevBar = sorted[idx - 1];
    const nextBar = sorted[idx + 1];
    dragRef.current = {
      id: bar.id,
      edge,
      startX: e.clientX,
      orig: edge === 'left' ? bar.starts : bar.ends,
      min: edge === 'left' ? (prevBar ? prevBar.ends : 0) : bar.starts + MIN_DUR,
      max: edge === 'left' ? bar.ends - MIN_DUR : nextBar ? nextBar.starts : duration + 5,
    };
    window.addEventListener('pointermove', onDragMove);
    window.addEventListener('pointerup', onDragUp);
  };
  const onDragMove = (e: PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dt = (e.clientX - d.startX) / pxPerSec;
    const val = Math.min(d.max, Math.max(d.min, d.orig + dt));
    updateBar(d.id, d.edge === 'left' ? { starts: val } : { ends: val });
  };
  const onDragUp = () => {
    dragRef.current = null;
    window.removeEventListener('pointermove', onDragMove);
    window.removeEventListener('pointerup', onDragUp);
    commitHistory();
  };

  // ---- move the whole bar (time + pitch) by dragging its body ----
  const moveRef = React.useRef<{
    id: number;
    startX: number;
    startY: number;
    origStarts: number;
    dur: number;
    origMidi: number;
    moved: boolean;
  } | null>(null);

  const onBarDown = (e: React.PointerEvent, bar: PitchBar) => {
    setSelectedId(bar.id);
    snapshot();
    moveRef.current = {
      id: bar.id,
      startX: e.clientX,
      startY: e.clientY,
      origStarts: bar.starts,
      dur: bar.ends - bar.starts,
      origMidi: freqToMidi(bar.avgs),
      moved: false,
    };
    window.addEventListener('pointermove', onMoveMove);
    window.addEventListener('pointerup', onMoveUp);
  };
  const onMoveMove = (e: PointerEvent) => {
    const m = moveRef.current;
    if (!m) return;
    const dx = e.clientX - m.startX;
    const dy = e.clientY - m.startY;
    if (!m.moved && Math.abs(dx) < 3 && Math.abs(dy) < 3) return; // ignore tiny jitters
    m.moved = true;
    const newStarts = Math.max(0, m.origStarts + dx / pxPerSec);
    const newMidi = Math.round(m.origMidi - dy / pxPerSemi); // snap to semitone
    updateBar(m.id, {
      starts: +newStarts.toFixed(3),
      ends: +(newStarts + m.dur).toFixed(3),
      avgs: midiToFreq(newMidi),
      note: midiToName(newMidi),
    });
  };
  const onMoveUp = () => {
    moveRef.current = null;
    window.removeEventListener('pointermove', onMoveMove);
    window.removeEventListener('pointerup', onMoveUp);
    commitHistory();
  };

  // keyboard: undo/redo (anywhere), Delete removes, Esc deselects
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (selectedId == null) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Delete' || e.key === 'Backspace') deleteBar(selectedId);
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, past, future]);

  // Export the edited pitch map as a downloadable JSON file (same shape as input).
  // Find the bars_metadata object in any of the shapes the backend / exports use.
  const extractBarsMetadata = (parsed: any): any | null => {
    if (!parsed) return null;
    if (Array.isArray(parsed)) return { bars_data: parsed }; // bare bars array
    if (Array.isArray(parsed.bars_data)) return parsed; // { bars_data, ... }
    if (parsed.bars_metadata?.bars_data) return parsed.bars_metadata; // { bars_metadata: {...} }
    if (Array.isArray(parsed.resources)) {
      // backend GET shape: { resources: [ { bars_metadata: { bars_data } } ] }
      const r = parsed.resources.find((x: any) => x?.bars_metadata?.bars_data);
      if (r) return r.bars_metadata;
    }
    return null;
  };

  // Load bars from an uploaded JSON file.
  const handleUploadJson = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const bm = extractBarsMetadata(parsed);
        const newBars = bm?.bars_data;
        if (!Array.isArray(newBars) || !newBars.length) {
          const keys = parsed && typeof parsed === 'object' ? Object.keys(parsed).join(', ') : typeof parsed;
          throw new Error(`couldn't find bars_data (top-level keys: ${keys})`);
        }
        pushHistory();
        setBars([...newBars].sort((a: PitchBar, b: PitchBar) => a.starts - b.starts));
        setExtra({
          student_sections_data: bm.student_sections_data,
          student_section_weights: bm.student_section_weights,
        });
        setSelectedId(null);
        toast.success(`Loaded ${newBars.length} bars from JSON.`);
      } catch (e: any) {
        console.error('Upload JSON failed:', e);
        toast.error(`Invalid JSON: ${e?.message || 'could not parse'}`);
      }
    };
    reader.onerror = () => toast.error('Could not read the file.');
    reader.readAsText(file);
  };

  const downloadJson = () => {
    const out = {
      bars_data: sorted,
      student_sections_data: extra.student_sections_data,
      student_section_weights: extra.student_section_weights,
    };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pitchmap_${pitchMap.moduleId ?? 'edited'}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Pitch map JSON downloaded.');
  };

  const downloadAudio = async () => {
    if (!pitchMap.audioUrl) {
      toast.error('No audio file is available for this variant.');
      return;
    }

    const fileName = audioFileName || `practice_audio_${pitchMap.moduleId ?? 'recording'}`;
    const triggerDownload = (href: string, openInNewTab = false) => {
      const a = document.createElement('a');
      a.href = href;
      a.download = fileName;
      if (openInNewTab) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    setDownloadingAudio(true);
    try {
      const response = await fetch(pitchMap.audioUrl);
      if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      triggerDownload(url);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error('Audio download failed:', e);
      triggerDownload(pitchMap.audioUrl, true);
      toast.info('Opened audio in a new tab because direct download is blocked by CORS.');
    } finally {
      setDownloadingAudio(false);
    }
  };

  const handleSave = async () => {
    const id = pitchMap.moduleId;
    if (id == null || id === '' || Number.isNaN(Number(id))) {
      toast.error('This is a preview — open a real module to save.');
      return;
    }
    if (!sorted.length) {
      toast.error('Nothing to save — upload a pitch-map JSON first.');
      return;
    }
    if (!canSavePitchMap) {
      toast.error('Select a pitch and tempo before saving this pitch map.');
      return;
    }
    setSaving(true);
    try {
      await saveVerifiedAI(id, { ...pitchMap, ...extra, bars_data: sorted }, saveTarget);
      toast.success('Pitch map for this resource has been saved.');
    } catch (e: any) {
      console.error('Save bars-metadata failed:', e?.response?.status, e?.response?.data, e);
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.statusText ||
        e?.message ||
        'Unknown error';
      toast.error(`Failed to save: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const handleVerificationChange = async () => {
    const id = pitchMap.moduleId;
    if (id == null || id === '' || Number.isNaN(Number(id))) {
      toast.error('This is a preview — open a real module to update verification.');
      return;
    }

    const nextVerified = !isVerified;
    setVerifying(true);
    try {
      await setPracticeVerification(id, nextVerified);
      toast.success(nextVerified ? 'Practice marked as verified.' : 'Practice unverified.');
      setVerifyModalOpen(false);
      onSaved();
    } catch (e: any) {
      console.error('Update practice verification failed:', e?.response?.status, e?.response?.data, e);
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.statusText ||
        e?.message ||
        'Unknown error';
      toast.error(`Failed to update verification: ${detail}`);
    } finally {
      setVerifying(false);
    }
  };

  const handleResourceVerificationChange = async () => {
    if (!canVerifyResource) {
      toast.error('No resource is selected for verification.');
      return;
    }

    const nextVerified = !isResourceVerified;
    setVerifying(true);
    try {
      await setResourceVerification(resourceId, nextVerified);
      toast.success(nextVerified ? 'Resource verified.' : 'Resource unverified.');
      onResourceVerificationChanged?.(nextVerified);
    } catch (e: any) {
      console.error('Update resource verification failed:', e?.response?.status, e?.response?.data, e);
      const detail =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.response?.statusText ||
        e?.message ||
        'Unknown error';
      toast.error(`Failed to update resource verification: ${detail}`);
    } finally {
      setVerifying(false);
    }
  };

  // gridlines at each semitone
  const gridlines: number[] = [];
  for (let m = Math.ceil(minMidi); m <= Math.floor(maxMidi); m++) gridlines.push(m);

  return (
    <div className="pme">
      {/* Toolbar */}
      <div className="pme__toolbar">
        <div className="pme__legend">
          {variants && variants.length > 0 && (
            <label className="pme__variant">
              <span className="pme__variant-label">Variant</span>
              <Input
                bsSize="sm"
                type="select"
                value={String(activeVariantId ?? '')}
                onChange={(e) => onSelectVariant?.(e.target.value)}
                disabled={variants.length < 2}
              >
                {variants.map((v) => (
                  <option key={v.id} value={String(v.id)}>
                    {v.label}
                  </option>
                ))}
              </Input>
            </label>
          )}
          <span className="pme__swatch pme__swatch--teacher" /> Teacher
          <span className="pme__swatch pme__swatch--student" /> Student
        </div>
        <div className="pme__tools">
          <span className="pme__zoom-group">
            <button
              className="pme__icon-btn"
              title="Undo (Ctrl/Cmd+Z)"
              disabled={!past.length}
              onClick={undo}
            >
              <FiRotateCcw />
            </button>
            <button
              className="pme__icon-btn"
              title="Redo (Ctrl/Cmd+Shift+Z)"
              disabled={!future.length}
              onClick={redo}
            >
              <FiRotateCw />
            </button>
          </span>
          <span className="pme__zoom-group">
            <button
              className="pme__add-bar"
              title={isResourceVerified ? 'Unverify this resource' : 'Verify this resource'}
              onClick={handleResourceVerificationChange}
              disabled={saving || verifying || !canVerifyResource}
            >
              {verifying ? (
                <Spinner size="sm" />
              ) : isResourceVerified ? (
                'Unverify Resource'
              ) : (
                'Verify Resource'
              )}
            </button>
          </span>
          <span className="pme__count">{sorted.length} bars</span>
          <span className="pme__zoom-group">
            <span className="pme__zoom-label">Time</span>
            <button className="pme__icon-btn" title="Time zoom out" onClick={() => setPxPerSec((z) => Math.max(15, z - 15))}>
              <FiZoomOut />
            </button>
            <button className="pme__icon-btn" title="Time zoom in" onClick={() => setPxPerSec((z) => Math.min(240, z + 15))}>
              <FiZoomIn />
            </button>
          </span>
          <span className="pme__zoom-group">
            <span className="pme__zoom-label">Pitch</span>
            <button className="pme__icon-btn" title="Pitch zoom out" onClick={() => setPitchZoom((z) => Math.max(0.5, z / 1.25))}>
              <FiZoomOut />
            </button>
            <button className="pme__icon-btn" title="Pitch zoom in" onClick={() => setPitchZoom((z) => Math.min(4, z * 1.25))}>
              <FiZoomIn />
            </button>
          </span>
          <button
            className="pme__icon-btn"
            title="Reset zoom"
            onClick={() => {
              setPxPerSec(DEFAULT_PX_PER_SEC);
              setPitchZoom(1);
            }}
          >
            <FiMaximize2 />
          </button>
        </div>
      </div>

      <div className="pme__body">
        <div className="pme__main">
        {/* Chart */}
        <div className="pme__chart-scroll" ref={scrollRef}>
          {!hasBars && (
            <div className="pme__empty">
              <p>No pitch bars yet.</p>
              <div className="pme__empty-actions">
                <Button color="primary" outline size="sm" onClick={() => fileInputRef.current?.click()}>
                  <FiUpload /> Upload PitchMap
                </Button>
              </div>
            </div>
          )}
          <svg
            className="pme__chart"
            width={chartWidth}
            height={chartHeight}
            onClick={() => setSelectedId(null)}
          >
            {/* semitone gridlines + note labels */}
            {gridlines.map((m) => (
              <g key={m}>
                <line
                  x1={0}
                  x2={chartWidth}
                  y1={yOf(m)}
                  y2={yOf(m)}
                  className="pme__grid"
                />
                <text x={4} y={yOf(m) - 2} className="pme__grid-label">
                  {Math.round(midiToFreq(m))} Hz
                </text>
              </g>
            ))}

            {/* bars */}
            {sorted.map((b, idx) => {
              const x = xOf(b.starts);
              const w = Math.max(2, xOf(b.ends) - xOf(b.starts));
              const y = yOf(barMidi(b)) - BAR_H / 2;
              const isSel = b.id === selectedId;
              const isMirror = b.id === mirrorId;
              return (
                <g
                  key={b.id}
                  className={`pme__bar-g ${isSel ? 'is-selected' : ''} ${
                    isMirror ? 'is-mirror' : ''
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(b.id);
                  }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={BAR_H}
                    rx={BAR_H / 2}
                    className={`pme__bar pme__bar--${b.sangBy}`}
                    onPointerDown={(e) => onBarDown(e, b)}
                  />
                  {w > 24 && (
                    <text x={x + w / 2} y={y - 4} className="pme__bar-note">
                      {displayNote(b)}
                    </text>
                  )}
                  {/* resize handles when selected */}
                  {isSel && (
                    <>
                      <rect
                        x={x - 3}
                        y={y - 2}
                        width={6}
                        height={BAR_H + 4}
                        className="pme__handle"
                        onPointerDown={(e) => onHandleDown(e, b, 'left', idx)}
                      />
                      <rect
                        x={x + w - 3}
                        y={y - 2}
                        width={6}
                        height={BAR_H + 4}
                        className="pme__handle"
                        onPointerDown={(e) => onHandleDown(e, b, 'right', idx)}
                      />
                    </>
                  )}
                </g>
              );
            })}

            {/* playhead */}
            <line
              x1={xOf(playhead)}
              x2={xOf(playhead)}
              y1={0}
              y2={chartHeight}
              className="pme__playhead"
            />
            <circle cx={xOf(playhead)} cy={PAD_TOP} r={5} className="pme__playhead-dot" />
          </svg>
        </div>

        {/* lyric strip: current lyric only (chart width) */}
        {hasBars && (
          <div className="pme__lyrics">
            <span className="pme__lyric pme__lyric--current">
              {lyricOf(sorted[activeIndex])}
            </span>
          </div>
        )}
        </div>

        {/* Inspector */}
        <aside className="pme__inspector">
          {!selected ? (
            <div className="pme__hint">
              <Button color="primary" size="sm" block onClick={addBar} className="mb-3">
                <FiPlus /> Add Bar
              </Button>
              <p>Click a bar to edit it.</p>
              <ul>
                <li>Drag a bar’s <strong>body</strong> to move it (time + pitch).</li>
                <li>Drag a selected bar’s <strong>edges</strong> to resize.</li>
                <li>Use <strong>Merge</strong> to combine with a neighbor.</li>
                <li>Fine-tune pitch with <strong>cents</strong> (±10).</li>
                <li><kbd>Delete</kbd> removes, <kbd>Esc</kbd> deselects.</li>
              </ul>
            </div>
          ) : (
            <div className="pme__fields">
              <div className="pme__field-head">
                <span className={`pme__tag pme__tag--${selected.sangBy}`}>{selected.sangBy}</span>
                <span className="pme__bar-id">#{selected.id}</span>
              </div>

              <label className="pme__field">
                <span>Sung by</span>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selected.sangBy}
                  onFocus={snapshot}
                  onBlur={commitHistory}
                  onChange={(e) =>
                    updateBar(selected.id, { sangBy: e.target.value as PitchBar['sangBy'] })
                  }
                >
                  <option value="teacher">teacher</option>
                  <option value="student">student</option>
                </Input>
              </label>

              {selected.sangBy === 'teacher' && (
                <p className="pme__mirror-note">
                  {mirrorId != null
                    ? 'Edits mirror to the paired student bar (highlighted).'
                    : 'No paired student bar to mirror.'}
                </p>
              )}

              <label className="pme__field">
                <span>Note</span>
                <Input
                  bsSize="sm"
                  value={selected.note}
                  placeholder={midiToName(barMidi(selected))}
                  onFocus={snapshot}
                  onBlur={commitHistory}
                  onChange={(e) => updateBar(selected.id, { note: e.target.value })}
                />
                <small>≈ {midiToName(barMidi(selected))} · {selected.avgs.toFixed(1)} Hz</small>
              </label>

              <label className="pme__field">
                <span>Pitch fine-tune: {(selected.cents || 0) > 0 ? '+' : ''}{selected.cents || 0} cents</span>
                <input
                  type="range"
                  min={-10}
                  max={10}
                  step={1}
                  value={selected.cents || 0}
                  onPointerDown={snapshot}
                  onBlur={commitHistory}
                  onPointerUp={commitHistory}
                  onChange={(e) => updateBar(selected.id, { cents: Number(e.target.value) })}
                />
              </label>

              <div className="pme__field-row">
                <label className="pme__field">
                  <span>Start (s)</span>
                  <Input
                    bsSize="sm"
                    type="number"
                    step={0.01}
                    value={selected.starts.toFixed(2)}
                    onFocus={snapshot}
                    onBlur={commitHistory}
                    onChange={(e) => updateBar(selected.id, { starts: Number(e.target.value) })}
                  />
                </label>
                <label className="pme__field">
                  <span>End (s)</span>
                  <Input
                    bsSize="sm"
                    type="number"
                    step={0.01}
                    value={selected.ends.toFixed(2)}
                    onFocus={snapshot}
                    onBlur={commitHistory}
                    onChange={(e) => updateBar(selected.id, { ends: Number(e.target.value) })}
                  />
                </label>
              </div>

              <label className="pme__field">
                <span>Lyric</span>
                <Input
                  bsSize="sm"
                  value={selected.lyric}
                  onFocus={snapshot}
                  onBlur={commitHistory}
                  onChange={(e) => updateBar(selected.id, { lyric: e.target.value })}
                />
              </label>

              {selected.lyrics_hindi != null && (
                <label className="pme__field">
                  <span>Lyric (Hindi)</span>
                  <Input
                    bsSize="sm"
                    value={selected.lyrics_hindi}
                    onFocus={snapshot}
                    onBlur={commitHistory}
                    onChange={(e) => updateBar(selected.id, { lyrics_hindi: e.target.value })}
                  />
                </label>
              )}

              <div className="pme__actions">
                <Button
                  size="sm"
                  outline
                  disabled={selectedIndex <= 0}
                  onClick={() => mergeAt(selectedIndex, -1)}
                >
                  <FiChevronLeft /> Merge prev
                </Button>
                <Button
                  size="sm"
                  outline
                  disabled={selectedIndex >= sorted.length - 1}
                  onClick={() => mergeAt(selectedIndex, 1)}
                >
                  Merge next <FiChevronRight />
                </Button>
              </div>
              <Button color="danger" size="sm" outline block onClick={() => deleteBar(selected.id)}>
                <FiTrash2 /> Delete bar
              </Button>
            </div>
          )}
        </aside>
      </div>

      {/* hidden audio element drives playback when a recording is present */}
      {hasAudio && (
        <audio
          ref={audioRef}
          src={pitchMap.audioUrl}
          preload="auto"
          onLoadedMetadata={(e) => setAudioDur(e.currentTarget.duration || 0)}
          onEnded={stop}
          onError={() => {
            console.error('Audio failed to load:', pitchMap.audioUrl);
            toast.error('Audio file failed to load.');
          }}
        />
      )}

      {/* Playback + save */}
      <div className="pme__footer">
        <button className="pme__play" onClick={() => (playing ? stop() : play())}>
          {playing ? <FiPause /> : <FiPlay />}
        </button>
        <span className="pme__time">{fmtTime(playhead)}</span>
        <input
          className="pme__scrub"
          type="range"
          min={0}
          max={duration}
          step={0.01}
          value={playhead}
          onChange={(e) => seek(Number(e.target.value))}
        />
        <span className="pme__time">{fmtTime(duration)}</span>
        <div className="pme__footer-actions">
          <Button
            color="secondary"
            outline
            onClick={downloadAudio}
            disabled={saving || verifying || downloadingAudio || !hasAudio}
          >
            {downloadingAudio ? <Spinner size="sm" /> : <FiDownload />} Download Audio
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUploadJson(f);
              e.target.value = '';
            }}
          />
          <Button
            color="secondary"
            outline
            onClick={() => fileInputRef.current?.click()}
            disabled={saving || verifying}
          >
            <FiUpload /> Upload PitchMap
          </Button>
          <Button
            color="secondary"
            outline
            onClick={downloadJson}
            disabled={saving || verifying || !hasBars}
          >
            <FiDownload /> Download PitchMap
          </Button>
          {isAIRecording && (
            <Button
              color={isVerified ? 'danger' : 'success'}
              outline
              onClick={() => setVerifyModalOpen(true)}
              disabled={saving || verifying}
            >
              {isVerified ? 'Unverify' : 'Mark as Verified'}
            </Button>
          )}
          <Button
            color="primary"
            onClick={handleSave}
            disabled={saving || verifying || !canSavePitchMap}
          >
            {saving ? <Spinner size="sm" /> : 'Save'}
          </Button>
        </div>
      </div>

      <Modal isOpen={verifyModalOpen} centered>
        <ModalHeader>{isVerified ? 'Unverify Practice' : 'Mark as Verified'}</ModalHeader>
        <ModalBody>
          Are you sure you want to {isVerified ? 'unverify' : 'mark'} this AI practice
          {isVerified ? '' : ' as verified'}?
        </ModalBody>
        <ModalFooter>
          <Button
            color={isVerified ? 'danger' : 'success'}
            onClick={handleVerificationChange}
            disabled={verifying}
          >
            {verifying ? <Spinner size="sm" /> : 'Yes'}
          </Button>
          <Button
            color="secondary"
            onClick={() => setVerifyModalOpen(false)}
            disabled={verifying}
          >
            No
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PitchMapEditor;
